import {Subject} from 'rxjs';
import {map, switchMap} from 'rxjs/internal/operators';

import {FileTypeValidatorKey, InArrayValidatorKey, RequiredValidatorKey} from '@spa/validators';

import {
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    ImportsService,
    ImportFileInputModel,
    ImportFileProcessResponseModel
} from '@spa/api';

import {Helper, SelectControl} from '../../../shared/classes';

import {FileImportFormErrorStatesEnum} from '../enums';

import {FileImportTypeControl} from '../../../../assets/descriptors';

export class FileImportForm {
    private readonly importsService: ImportsService;

    public documentFile: File;
    public documentSubject: Subject<string>;
    public documentErrorsSubject: Subject<string[]>;

    public documentTypeControl: SelectControl;

    constructor(importsService: ImportsService) {
        this.importsService = importsService;

        this.documentFile = null;
        this.documentSubject = new Subject();
        this.documentErrorsSubject = new Subject();

        this.documentTypeControl = FileImportTypeControl();
    }

    private validateControl(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        const formsControlValue = this.documentTypeControl.value;

        if (!formsControlValue) {
            this.documentTypeControl.setError(messageFunction(this.documentTypeControl.placeholder)[RequiredValidatorKey]);

            return false;
        }

        const documentTypeIds = Helper.enumsHelper().getFileImportDocumentTypes().getTypeKeys();

        if (!documentTypeIds.includes(formsControlValue)) {
            this.documentTypeControl.setError(messageFunction(this.documentTypeControl.placeholder)[InArrayValidatorKey]);

            return false;
        }

        return true;
    }

    onDocumentUpload(file: File): void {
        this.documentFile = file;
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        const documentSubjectErrors = Helper.validateFile(this.documentFile, messageFunction, true);

        if (this.documentFile && !Helper.filesHelper().getExcelFileMimeTypes().includes(this.documentFile.type)) {
            documentSubjectErrors.push(messageFunction()[FileTypeValidatorKey]);
        }

        this.documentErrorsSubject.next(documentSubjectErrors);

        return ![(documentSubjectErrors.length === 0), this.validateControl(messageFunction)].includes(false);
    }

    createRequest(): ServerResponse<ImportFileProcessResponseModel> {
        return Helper.filesHelper().convertFileToBase64(this.documentFile).pipe(
            switchMap((base64DataUrl: string) => {
                if (!base64DataUrl) {
                    return Helper.errorsHelper().createBadRequestResponseObservable(FileImportFormErrorStatesEnum.ConvertFileFailed);
                }

                const data = Helper.filesHelper().getDataFromBase64DataUrl(base64DataUrl, this.documentFile.type);
                const importFileInputModel = new ImportFileInputModel(Number(this.documentTypeControl.value),
                    this.documentFile.name,
                    data);

                return this.importsService.validateImport(importFileInputModel).pipe(
                    switchMap((validationResponse: ServerResponseData<number>) => {
                        if ([HttpStatusCodesEnum.Ok, HttpStatusCodesEnum.BadRequest].indexOf(validationResponse.status) < 0) {
                            return Helper.errorsHelper().createErrorResponseObservable<number>(
                                validationResponse,
                                FileImportFormErrorStatesEnum.FileValidationFailed
                            );
                        }

                        if (validationResponse.status === HttpStatusCodesEnum.BadRequest) {
                            return Helper.errorsHelper().createErrorResponseObservable<number>(
                                validationResponse,
                                FileImportFormErrorStatesEnum.FileValidationErrorsOccurred
                            );
                        }

                        const fileId = validationResponse.data as number;

                        return this.importsService.processImport(fileId).pipe(
                            map((processResponse: ServerResponseData<ImportFileProcessResponseModel>) => {
                                if (processResponse.status !== HttpStatusCodesEnum.Ok) {
                                    return Helper.errorsHelper().createErrorResponse<ImportFileProcessResponseModel>(
                                        processResponse,
                                        FileImportFormErrorStatesEnum.FileImportFailed
                                    );
                                }

                                this.documentFile = null;
                                this.documentSubject.next(null);
                                this.documentTypeControl.value = null;

                                return Helper.createSuccessResponse<ImportFileProcessResponseModel>(
                                    processResponse.data as ImportFileProcessResponseModel
                                );
                            })
                        );
                    })
                );
            })
        );
    }
}
